/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState, Fragment } from 'react'
import { useIntl } from 'react-intl'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiDescribedFormGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiForm,
  EuiFormRow,
  EuiLink,
  EuiSpacer,
  EuiSwitch,
  EuiText,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useDisableAuditLogs, useEnableAuditLogsConfiguration } from '@modules/audit-logs-api'

import SecurityDocsLink from '../SecurityDocsLink'

import DeploymentSelectField from './DeploymentSelectField'

import type { FunctionComponent } from 'react'

interface Props {
  onClose: () => void
  organizationId: string
  deploymentId?: string
}

const ConfigureAuditTrailFlyout: FunctionComponent<Props> = ({
  onClose,
  organizationId,
  deploymentId,
}) => {
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string | null>(null)
  const [isEnabled, setIsEnabled] = useState(true)
  const { formatMessage } = useIntl()
  const { mutate: enableAuditLogs } = useEnableAuditLogsConfiguration()
  const { mutate: disableData } = useDisableAuditLogs({ organizationId })

  const handleSave = () => {
    if (!isEnabled) {
      disableData()
      onClose()
      return
    }

    if (selectedDeploymentId === null) {
      return onClose()
    }

    enableAuditLogs({ payload: { deployment_id: selectedDeploymentId }, organizationId })
    onClose()
  }

  const canSave = isEnabled ? selectedDeploymentId !== null : true

  return (
    <EuiFlyout onClose={onClose} size='m' aria-labelledby='configureAuditTrailTitle'>
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2 id='configureAuditTrailTitle'>
            <FormattedMessage
              id='organization.security.cloud-audit-trail.configure-title'
              defaultMessage='Configure Cloud audit trail'
            />
          </h2>
        </EuiTitle>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        <EuiText color='subdued' size='s'>
          <FormattedMessage
            id='organization.security.cloud-audit-trail.configure-description'
            defaultMessage='Keep track of actions performed on your Elastic Cloud infrastructure by members of your organization. <lm>Learn more</lm>'
            values={{
              lm: (msg) => (
                <SecurityDocsLink fragment='ec-cloud-audit-trail'>{msg}</SecurityDocsLink>
              ),
            }}
          />
        </EuiText>

        <EuiSpacer size='l' />

        <EuiForm component='form'>
          <Fragment>
            <EuiDescribedFormGroup
              title={
                <h3 id='switch-label'>
                  <FormattedMessage
                    id='organization.security.cloud-audit-trail.destination-toggle-label'
                    defaultMessage='Cloud audit trail destination'
                  />
                </h3>
              }
              description={
                <FormattedMessage
                  id='organization.security.cloud-audit-trail.destination-description'
                  defaultMessage='When enabled, audit trail data will be shipped to the destination selected below.'
                />
              }
            >
              <EuiFormRow>
                <EuiSwitch
                  label={formatMessage({
                    id: 'organization.security.cloud-audit-trail.enable-switch-label',
                    defaultMessage: 'Enable Cloud audit trail',
                  })}
                  showLabel={false}
                  aria-labelledby='switch-label'
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                />
              </EuiFormRow>
            </EuiDescribedFormGroup>
            <EuiSpacer size='m' />

            <EuiSpacer size='m' />

            {isEnabled && (
              <EuiFormRow
                label={
                  <FormattedMessage
                    id='organization.security.cloud-audit-trail.hosted-deployment-label'
                    defaultMessage='Hosted deployment'
                  />
                }
                fullWidth={true}
                helpText={
                  <EuiLink href='#' target='_blank'>
                    <FormattedMessage
                      id='organization.security.cloud-audit-trail.create-new-link'
                      defaultMessage='Create new'
                    />
                  </EuiLink>
                }
              >
                <DeploymentSelectField
                  data-test-id='cloud-audit-trail-deployment-picker'
                  value={selectedDeploymentId ?? (deploymentId || null)}
                  onChange={setSelectedDeploymentId}
                  placeholder='Select'
                />
              </EuiFormRow>
            )}
          </Fragment>
        </EuiForm>
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty onClick={onClose} data-test-id='cancel-button'>
              <FormattedMessage
                id='organization.security.cloud-audit-trail.cancel'
                defaultMessage='Cancel'
              />
            </EuiButtonEmpty>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton fill={true} disabled={!canSave} onClick={handleSave}>
              <FormattedMessage
                id='organization.security.cloud-audit-trail.save'
                defaultMessage='Save'
              />
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default ConfigureAuditTrailFlyout
